/**
 * ActiveContextOverrideBanner — composer-side hint shown when an
 * implicit sidebar context (open inbox thread, calendar event, document,
 * canvas…) is being SUPPRESSED by an explicit scope (folder ambito,
 * sticky scoped docs, Connect Space). Jun 17, 2026.
 *
 * Why it exists
 * -------------
 * Before this hint, the precedence rule lived only in ``useSendQuery``
 * (drop ``active_context`` from the wire when the user has set explicit
 * scope). The user observed that "have inbox open + 1-folder ambito"
 * answered about the inbox, not the folder — the implicit signal silently
 * outranked the explicit one. The send-time fix flipped the precedence;
 * this banner makes the override visible so the user is never surprised:
 * they SEE which surface is being grounded.
 *
 * UX shape — pencil pattern (matches GuideBox + ChartNeedsConnectorState):
 *   ┌───────────────────────────────────────────────────────────┐
 *   │ ▎ Ambito 1 cartella attivo · "R: Ando<>Brini" non userà   │
 *   │ ▎ il pannello aperto a destra in questa risposta.         │
 *   └───────────────────────────────────────────────────────────┘
 *
 * Self-hides when there's nothing to say (no active context OR no
 * explicit scope) — single component, single source of truth, no
 * caller-side gating needed.
 */
import { ArrowRight } from "lucide-react";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";

import { cn } from "@ando/ui";

import { useActiveContextOverride } from "../hooks/useActiveContextOverride";
import type { ActiveContextKind } from "../types";

const KIND_LABEL_IT: Record<ActiveContextKind, ReturnType<typeof msg>> = {
  calendar_event: msg`evento calendario`,
  document: msg`documento`,
  inbox_thread: msg`email`,
  source_citation: msg`citazione`,
  canvas: msg`canvas`,
  connect_space: msg`spazio Connect`,
  flow: msg`flow`,
};

export interface ActiveContextOverrideBannerProps {
  readonly className?: string;
}

export const ActiveContextOverrideBanner: React.FC<
  ActiveContextOverrideBannerProps
> = ({ className }) => {
  const { active, isOverridden, isDismissed } = useActiveContextOverride();
  const { i18n } = useLingui();
  // When the user has dismissed the ambient chip there is nothing left to
  // "override" — the open entity is already gone from the send envelope by
  // explicit choice. Suppress the banner so the two hints never contradict.
  if (!active || !isOverridden || isDismissed) return null;

  const kindMsg = KIND_LABEL_IT[active.kind];
  const kindLabel = kindMsg ? i18n._(kindMsg) : i18n._(msg`elemento`);
  const title = active.title?.trim() || kindLabel;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "flex items-center gap-2 rounded-md border border-hairline border-l-2 border-l-ando-orange/55 bg-card/60 px-2.5 py-1.5 text-[11.5px] text-ink-2",
        className,
      )}
    >
      <span className="shrink-0 font-medium text-ink-1">
        <Trans>Ambito attivo</Trans>
      </span>
      <ArrowRight className="h-3 w-3 shrink-0 text-ink-3" aria-hidden />
      <span className="min-w-0 truncate">
        <Trans>
          Il {kindLabel} aperto{" "}
          <span className="font-medium text-ink-1" title={title}>
            “{title}”
          </span>{" "}
          non guiderà questa risposta.
        </Trans>
      </span>
    </div>
  );
};
ActiveContextOverrideBanner.displayName = "ActiveContextOverrideBanner";
